/**
 * The one ordering for subfield labels, shared by every surface that lists them.
 *
 * A subfield label is free text. Real Fall 2026 venues carry `1`…`12`, `A`…`F`,
 * `VP1`/`VP2`, `Area H`, `FIS Upper 1`, `Field-2`, `LCHS JV` and `Marshall`, so
 * there is no column type that would sort them correctly on its own:
 *
 * - `ORDER BY label` against the TEXT column reads `1, 10, 11, 12, 2, 3` — the
 *   defect this replaces, and one that hit every surface listing subfields
 *   (permits, eligibility, sunset, the move dialog), not just one of them.
 * - `CAST(label AS INTEGER)` sorts every non-numeric label to 0, collapsing
 *   `A`…`F`, `VP1` and `Area H` into one indistinguishable block.
 *
 * ⚠️ **Sort in TypeScript, not in SQL, and do not "optimize" this back into an
 * ORDER BY expression.** SQLite has no natural collation, so the SQL form would
 * be a hand-rolled CASE expression — and there is no SQLite in the vitest
 * harness (see CLAUDE.md). DAO tests assert the SQL string that was built, never
 * the rows it returns, so a sort expressed in SQL here would ship on a test that
 * cannot fail. The comparator below is a pure module precisely so its behavior
 * is checkable, and the DAO reads keep their `ORDER BY` only as a deterministic
 * input to this stable sort.
 *
 * The locale is pinned to `en` rather than left to the runtime default: this
 * runs in workerd, in Node under vitest, and in five browsers, and an
 * implicit-locale collator is free to disagree between them.
 */
const COLLATOR = new Intl.Collator('en', { numeric: true })

/** Natural-order comparator for any operator-facing name: numeric runs compare
 *  as numbers (`2` before `10`, `VP2` before `VP10`), everything else as text,
 *  and numbered entries sort ahead of named ones. Pass straight to `.sort()`.
 *
 *  Subfield labels are the motivating case (above), but venue titles and layout
 *  names want the same treatment — `Victory Park 2` before `Victory Park 10` —
 *  and `apps/fields` was already hand-rolling this for layout names. One
 *  comparator, so the two cannot drift apart. */
export function compareNatural(a: string, b: string): number {
  return COLLATOR.compare(a, b)
}
